import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Home as HomeIcon, Leaf, ShieldCheck, Waves, ArrowRight } from "lucide-react";
import { heroSlides, company, welcome, dreamsSection, highlights, img, video, serviceAreas } from "../mock";
import Seo from "../components/Seo";

const iconMap = { Home: HomeIcon, Leaf, ShieldCheck, Waves };

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".mh-reveal");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); obs.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};

const Home = () => {
  const [active, setActive] = useState(0);
  const timer = useRef(null);
  useReveal();

  useEffect(() => {
    timer.current = setInterval(() => setActive((a) => (a + 1) % heroSlides.length), 5500);
    return () => clearInterval(timer.current);
  }, []);

  return (
    <div>
      <Seo
        description="Monarch Homes of Brevard is an award-winning custom home builder serving Brevard County and the Space Coast - Melbourne Beach, Indialantic, Indian Harbour Beach, Satellite Beach, Viera and Melbourne. Family-owned, building energy-efficient custom homes for over 40 years."
        keywords="Monarch Homes of Brevard, Custom Home Builder Brevard, Melbourne Beach Custom Home Builder, Indialantic Custom Home Builder, Indian Harbour Beach Custom Home Builder, Satellite Beach Custom Home Builder, Viera Custom Home Builder"
        path="/"
      />
      {/* HERO */}
      <section className="mh-hero">
        {heroSlides.map((src, i) => (
          <div key={i} className={`mh-hero-slide ${i === active ? "active" : ""}`} style={{ backgroundImage: `url(${src})` }} />
        ))}
        <div className="mh-hero-content">
          <div className="mh-hero-eyebrow">Brevard County&rsquo;s Premier Custom Home Builder</div>
          <h1>We Build
            <span className="mh-hero-script">Dream Homes</span>
          </h1>
          <p className="mh-hero-sub">{company.tagline}</p>
          <div className="mh-hero-actions">
            <Link to="/available-homes" className="mh-cta-btn">View Available Homes <ArrowRight size={16} /></Link>
            <Link to="/contact" className="mh-btn-ghost-light">Start Your Build</Link>
          </div>
        </div>
        <div className="mh-hero-dots">
          {heroSlides.map((_, i) => (
            <button key={i} className={i === active ? "active" : ""} onClick={() => setActive(i)} aria-label={`Slide ${i + 1}`} />
          ))}
        </div>
      </section>

      {/* WELCOME */}
      <section className="mh-section">
        <div className="mh-container mh-welcome">
          <div className="mh-welcome-img mh-reveal">
            <img src={img.ext4} alt="A Monarch coastal custom home" />
            <div className="mh-welcome-badge">
              <strong>40+</strong>
              <span>Years Building</span>
            </div>
          </div>
          <div className="mh-welcome-text mh-reveal">
            <div className="mh-eyebrow">{welcome.eyebrow}</div>
            <h2>A Family Building Tradition On Florida&rsquo;s Space Coast</h2>
            <p>{welcome.body}</p>
            <div className="mh-welcome-actions">
              <Link to="/who-we-are" className="mh-btn-outline">Meet Our Family</Link>
              <Link to="/gallery" className="mh-btn-outline">View Our Work</Link>
            </div>
          </div>
        </div>
      </section>

      {/* VIDEO SHOWCASE */}
      <section className="mh-section cream tight">
        <div className="mh-container">
          <div className="mh-section-head mh-reveal">
            <div className="mh-eyebrow">Step Inside</div>
            <h2>See The Quality For Yourself</h2>
            <p>Take a walk through the craftsmanship, finishes and thoughtful design that define a Monarch home.</p>
            <div className="mh-divider" />
          </div>
          <div className="mh-video-wrap mh-reveal">
            <video src={video.src} poster={video.poster} controls playsInline preload="metadata" />
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="mh-section tight">
        <div className="mh-container">
          <div className="mh-section-head mh-reveal">
            <div className="mh-eyebrow">Why Build With Monarch</div>
            <h2>Quality Craftsmanship, Built To Last</h2>
            <div className="mh-divider" />
          </div>
          <div className="mh-highlights">
            {highlights.map((h, i) => {
              const Ic = iconMap[h.icon];
              return (
                <div key={i} className="mh-hi-card mh-reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                  <div className="mh-hi-icon"><Ic size={28} /></div>
                  <h3>{h.title}</h3>
                  <p>{h.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AREAS WE SERVE */}
      <section className="mh-section tight">
        <div className="mh-container">
          <div className="mh-section-head mh-reveal">
            <div className="mh-eyebrow">Areas We Serve</div>
            <h2>Your Custom Home Builder Across Brevard County</h2>
            <p>As a trusted custom home builder in Brevard County, Monarch Homes builds beautiful, energy-efficient custom homes throughout the Space Coast - including Melbourne Beach, Indialantic, Indian Harbour Beach, Satellite Beach, Viera and Melbourne.</p>
            <div className="mh-divider" />
          </div>
          <div className="mh-areas">
            {serviceAreas.map((area) => (
              <Link key={area} to="/contact" className="mh-area-pill">{area} Custom Home Builder</Link>
            ))}
          </div>
        </div>
      </section>

      {/* DREAMS CTA BAND */}
      <section className="mh-cta-band">
        <div className="mh-container mh-reveal">
          <h2>{dreamsSection.heading}</h2>
          <p>{dreamsSection.p1} {dreamsSection.p2}</p>
          <div className="mh-hero-actions">
            <Link to="/build-on-your-lot" className="mh-cta-btn">Build On Your Lot <ArrowRight size={16} /></Link>
            <Link to="/contact" className="mh-btn-ghost-light">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
